import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { useEffect, useMemo, useCallback } from "react";
import moment from 'moment';

// Controls
import { Modal, ModalBody, ModalFooter, Button } from "@gpstrackit/react-controls";

// Chart.js
import { 
    Chart as ChartJS, 
    ArcElement, 
    Tooltip, 
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    Title
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

// Actions
import {
    systemAlertGraphicViewOpenModal,
    systemAlertGraphicViewSaveFilters,
    systemAlertGraphicViewClearResult,
    searchGraphicAlertsByType,
    resetSystemAlertGraphicView,
} from 'Redux/actions';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Title);

// Severity color definitions for Bar Chart (fixed single colors)
const SEVERITY_COLORS = {
    High: '#DC2626',   // Red
    Medium: '#F97316', // Orange
    Low: '#EAB308'     // Yellow
};

// Generate distinct pastel colors with consistent saturation and lightness
// Uses golden angle (~137.5°) for optimal hue distribution to maximize visual distinction
const generateDistinctPastelColors = (count) => {
    const colors = [];
    const saturation = 65; // Consistent saturation for pastel look
    const lightness = 60;  // Consistent lightness for pastel look
    const goldenAngle = 137.508; // Golden angle for optimal color distribution
    
    for (let i = 0; i < count; i++) {
        // Use golden angle to distribute hues evenly and distinctly
        const hue = (i * goldenAngle) % 360;
        colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
    }
    
    return colors;
};

const GraphicView = (props) => {
    const { company, device, type, last, filters } = props;
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const { openModal, loading, result, previousFilters, error } = useSelector(state => state.systemAlertGraphicViewRedux);

    // Build current filters object for comparison
    const getCurrentFilters = useCallback(() => {
        return {
            companyId: company?.id,
            unitId: device?.id || null,
            type: type,
            lastId: type === 'last' ? (last?.id || null) : null,
            startDate: type !== 'last' ? (filters?.startDate || null) : null,
            endDate: type !== 'last' ? (filters?.endDate || null) : null,
        };
    }, [company?.id, device?.id, type, last?.id, filters?.startDate, filters?.endDate]);

    // Compare two filter objects
    const filtersHaveChanged = useCallback((prev, current) => {
        console.log("FILTERS CHANGED", {prev, current})
        if (!prev) return true; // No previous filters means first load
        return (
            prev.companyId !== current.companyId ||
            prev.unitId !== current.unitId ||
            prev.type !== current.type ||
            prev.lastId !== current.lastId ||
            prev.startDate !== current.startDate ||
            prev.endDate !== current.endDate
        );
    }, []);
    
    const closeModal = () => {
        dispatch(systemAlertGraphicViewOpenModal(false));
        // Note: Don't clear data on close - keep cached for next open
    };

    // Group alerts by Type and generate colors
    const { typeData, typeColorMap } = useMemo(() => {
        if (!result || result.length === 0) {
            return { typeData: null, typeColorMap: {} };
        }

        // Aggregate quantities by AlertType
        const typeAggregation = {};
        result.forEach(item => {
            const alertType = item.AlertType || 'Unknown';
            if (!typeAggregation[alertType]) {
                typeAggregation[alertType] = 0;
            }
            typeAggregation[alertType] += (item.Quantity || 0);
        });

        // Get unique types and generate colors
        const types = Object.keys(typeAggregation);
        const colors = generateDistinctPastelColors(types.length);
        
        // Create color map for types
        const colorMap = {};
        types.forEach((type, index) => {
            colorMap[type] = colors[index];
        });

        return {
            typeData: {
                labels: types,
                quantities: types.map(type => typeAggregation[type]),
                colors
            },
            typeColorMap: colorMap
        };
    }, [result]);

    // Prepare Pie chart data (alerts grouped by type)
    const pieChartData = useMemo(() => {
        if (!typeData) {
            return null;
        }

        return {
            labels: typeData.labels,
            datasets: [
                {
                    data: typeData.quantities,
                    backgroundColor: typeData.colors,
                    borderColor: typeData.colors.map(color => color),
                    borderWidth: 1,
                }
            ]
        };
    }, [typeData]);

    // Prepare Bar chart data (alerts by severity)
    const barChartData = useMemo(() => {
        if (!result || result.length === 0) {
            return null;
        }

        // Aggregate quantities by severity
        const severityCounts = { High: 0, Medium: 0, Low: 0 };
        result.forEach(item => {
            const severity = item.AlertSeverity || 'Medium';
            if (severityCounts.hasOwnProperty(severity)) {
                severityCounts[severity] += (item.Quantity || 0);
            } else {
                severityCounts.Medium += (item.Quantity || 0);
            }
        });

        return {
            labels: [
                messages['system_alert_severity_high'],
                messages['system_alert_severity_medium'],
                messages['system_alert_severity_low']
            ],
            datasets: [
                {
                    label: messages['common_quantity'],
                    data: [severityCounts.High, severityCounts.Medium, severityCounts.Low],
                    backgroundColor: [SEVERITY_COLORS.High, SEVERITY_COLORS.Medium, SEVERITY_COLORS.Low],
                    borderColor: [SEVERITY_COLORS.High, SEVERITY_COLORS.Medium, SEVERITY_COLORS.Low],
                    borderWidth: 1,
                }
            ],
            severityCounts
        };
    }, [result, messages]);

    const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    padding: 15,
                    usePointStyle: true,
                    font: {
                        size: 11
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${label}: ${value} (${percentage}%)`;
                    }
                }
            }
        }
    };

    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: true,
                text: messages['system_alert_logs_graphic_view_by_severity'],
                font: {
                    size: 14
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const value = context.parsed.y || 0;
                        return `${context.label}: ${value}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1
                }
            }
        }
    };

    // Calculate total alerts
    const totalAlerts = useMemo(() => {
        if (!result || result.length === 0) return 0;
        return result.reduce((sum, item) => sum + (item.Quantity || 0), 0);
    }, [result])


    // Cleanup on component unmount
    useEffect(() => {
        return () => {
            // Clear redux data when component dies
            dispatch(resetSystemAlertGraphicView());
        };
    }, [dispatch]);

    // Fetch data when modal opens - only if filters have changed
    useEffect(() => {
        if (openModal && company?.id) {
            const currentFilters = getCurrentFilters();
            
            // Check if filters have changed OR if previous query failed
            if (!filtersHaveChanged(previousFilters, currentFilters) && !error) {
                // Filters haven't changed and no error, use cached data
                return;
            }

            // Clear existing data before new search
            dispatch(systemAlertGraphicViewClearResult({
                openModal: true, // Keep modal open
                loading: false,
                result: null,
                previousFilters: previousFilters // Keep previous filters until new ones are saved
            }));

            const unitId = device?.id || null;
            if (type === 'last') {
                // Use limit filter
                dispatch(searchGraphicAlertsByType({
                    companyId: company.id,
                    unitId: unitId || null,
                    limit: parseInt(last?.id) || 20,
                    range: null
                }));
            } else {
                // Use date range filter
                const startDatetz = moment.tz(filters.startDate, company?.timezone).startOf("day");
                const endDatetz = moment.tz(filters.endDate, company?.timezone).endOf("day");
                const startUtc = startDatetz.clone().utc();
                const endUtc = endDatetz.clone().utc();
                dispatch(searchGraphicAlertsByType({
                    companyId: company.id,
                    unitId: unitId || null,
                    limit: null,
                    range: {
                        start: startUtc.format(),
                        end: endUtc.format()
                    }
                }));
            }

            // Save current filters for comparison on next open
            dispatch(systemAlertGraphicViewSaveFilters(currentFilters));
        }
    }, [openModal, company?.id, device?.id, type, last?.id, filters?.startDate, filters?.endDate, company?.timezone]);


    return (
        <Modal
            open={openModal}
            title={messages['system_alert_logs_graphic_view_title']}
            position="right"
            size="medium"
            handleClose={closeModal}
            loading={loading}
        >
            <ModalBody>
                <div style={{ padding: "10px 10px 30px 10px" }}>
                    {pieChartData && barChartData ? (
                        <>
                            {/* Total alerts header */}
                            <div style={{ 
                                textAlign: 'center', 
                                marginBottom: 15, 
                                fontSize: 14,
                                color: '#666'
                            }}>
                                {messages['system_alert_logs_graphic_view_total']}: <strong>{totalAlerts}</strong>
                            </div>

                            {/* Charts container - responsive flex layout */}
                            <div style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap',
                                gap: 20,
                                marginBottom: 20
                            }}>
                                {/* Pie Chart - Alerts by Type */}
                                <div style={{ 
                                    flex: '1 1 280px',
                                    minWidth: 250,
                                    height: 300,
                                    position: 'relative'
                                }}>
                                    <div style={{ 
                                        textAlign: 'center', 
                                        marginBottom: 10, 
                                        fontSize: 14, 
                                        fontWeight: 500 
                                    }}>
                                        {messages['system_alert_logs_graphic_view_by_type']}
                                    </div>
                                    <div style={{ height: 'calc(100% - 30px)' }}>
                                        <Pie data={pieChartData} options={pieChartOptions} />
                                    </div>
                                </div>

                                {/* Bar Chart - Alerts by Severity */}
                                <div style={{ 
                                    flex: '1 1 280px',
                                    minWidth: 250,
                                    height: 300,
                                    position: 'relative'
                                }}>
                                    <div style={{ height: '100%' }}>
                                        <Bar data={barChartData} options={barChartOptions} />
                                    </div>
                                </div>
                            </div>

                            {/* Table with scrolling and fixed footer */}
                            <div style={{ marginTop: 20 }}>
                                {/* Table wrapper with scroll */}
                                <div style={{ 
                                    maxHeight: 500, 
                                    overflowY: 'auto',
                                    border: '1px solid #ddd',
                                    borderBottom: 'none',
                                    borderRadius: '4px 4px 0 0'
                                }}>
                                    <table style={{ 
                                        width: '100%', 
                                        borderCollapse: 'collapse', 
                                        fontSize: 12 
                                    }}>
                                        <thead style={{ 
                                            position: 'sticky', 
                                            top: 0, 
                                            backgroundColor: '#f8f9fa',
                                            zIndex: 1
                                        }}>
                                            <tr>
                                                <th style={{ 
                                                    textAlign: 'left', 
                                                    padding: '10px 8px',
                                                    borderBottom: '2px solid #dee2e6',
                                                    fontWeight: 600
                                                }}>
                                                    {messages['system_alert_logs_tab_name']}
                                                </th>
                                                <th style={{ 
                                                    textAlign: 'left', 
                                                    padding: '10px 8px',
                                                    borderBottom: '2px solid #dee2e6',
                                                    fontWeight: 600
                                                }}>
                                                    {messages['system_alert_logs_tab_type']}
                                                </th>
                                                <th style={{ 
                                                    textAlign: 'left', 
                                                    padding: '10px 8px',
                                                    borderBottom: '2px solid #dee2e6',
                                                    fontWeight: 600
                                                }}>
                                                    {messages['system_alert_logs_tab_severity']}
                                                </th>
                                                <th style={{ 
                                                    textAlign: 'right', 
                                                    padding: '10px 8px',
                                                    borderBottom: '2px solid #dee2e6',
                                                    fontWeight: 600
                                                }}>
                                                    {messages['common_quantity']}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.map((item, index) => (
                                                <tr key={index} style={{ 
                                                    borderBottom: '1px solid #eee',
                                                    backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa'
                                                }}>
                                                    <td style={{ padding: '8px' }}>
                                                        {item.AlertName || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px' }}>
                                                        <span style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 6
                                                        }}>
                                                            <span style={{ 
                                                                display: 'inline-block',
                                                                width: 12,
                                                                height: 12,
                                                                backgroundColor: typeColorMap[item.AlertType] || '#ccc',
                                                                borderRadius: 2,
                                                                flexShrink: 0
                                                            }}></span>
                                                            {item.AlertType || '-'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '8px' }}>
                                                        <span style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 6
                                                        }}>
                                                            <span style={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: '50%',
                                                                backgroundColor: SEVERITY_COLORS[item.AlertSeverity] || SEVERITY_COLORS.Medium
                                                            }}></span>
                                                            {messages[`system_alert_severity_${item.AlertSeverity.toLowerCase()}`] || item.AlertSeverity || '-'}
                                                        </span>
                                                    </td>
                                                    <td style={{ 
                                                        textAlign: 'right', 
                                                        padding: '8px', 
                                                        fontWeight: 'bold' 
                                                    }}>
                                                        {item.Quantity || 0}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Fixed Footer */}
                                <div style={{
                                    backgroundColor: '#f1f5f9',
                                    border: '1px solid #ddd',
                                    borderRadius: '0 0 4px 4px',
                                    padding: '12px 8px',
                                    fontSize: 12
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 12
                                    }}>
                                        {/* Total */}
                                        <div style={{ fontWeight: 600 }}>
                                            {messages['system_alert_logs_graphic_view_total']}: {totalAlerts}
                                        </div>

                                        {/* Severity breakdown */}
                                        <div style={{
                                            display: 'flex',
                                            gap: 16,
                                            flexWrap: 'wrap'
                                        }}>
                                            <span style={{ 
                                                display: 'inline-flex', 
                                                alignItems: 'center', 
                                                gap: 6 
                                            }}>
                                                <span style={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    backgroundColor: SEVERITY_COLORS.High
                                                }}></span>
                                                <span>{messages['system_alert_severity_high']}: <strong>{barChartData.severityCounts.High}</strong></span>
                                            </span>
                                            <span style={{ 
                                                display: 'inline-flex', 
                                                alignItems: 'center', 
                                                gap: 6 
                                            }}>
                                                <span style={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    backgroundColor: SEVERITY_COLORS.Medium
                                                }}></span>
                                                <span>{messages['system_alert_severity_medium']}: <strong>{barChartData.severityCounts.Medium}</strong></span>
                                            </span>
                                            <span style={{ 
                                                display: 'inline-flex', 
                                                alignItems: 'center', 
                                                gap: 6 
                                            }}>
                                                <span style={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    backgroundColor: SEVERITY_COLORS.Low
                                                }}></span>
                                                <span>{messages['system_alert_severity_low']}: <strong>{barChartData.severityCounts.Low}</strong></span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div style={{ 
                            display: 'flex', 
                            width: '100%', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            marginTop: 50, 
                            opacity: 0.5 
                        }}>
                            {messages['common_no_information_to_display']}
                        </div>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    className="btn-dark-blue"
                    onClick={() => closeModal()}
                >
                    {messages['close']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default GraphicView;
